import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import {APPROVAL_TYPE} from '../../../constants';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../../forecast-app/shared/util/util';
import UpdateApprovalStatusMutation from '../../../mutations/UpdateApprovalStatusMutation';
import {
	showApprovalErrorModal,
	showPartialApprovalFailureModal,
} from '../../../components/timesheets/time-approval/ApprovalErrors';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {DatePicker, FlexColumn, Modal, Text} from '@forecast-it/design-system';
import moment from 'moment';

/**
 * Co-located "open" API for this modal (per this project's modal conventions).
 * @param {{
 *   selectedPersonId: string,
 *   selectedPersonName?: string,
 *   currentViewingDate: moment.Moment,
 *   lockedDate?: moment.Moment,
 *   companyLockedDate?: moment.Moment,
 * }} props
 */
export const showUnsubmitTimeApprovalModal = props => {
	showModal({
		type: MODAL_TYPE.UNSUBMIT_TIME_APPROVAL,
		...props,
	});
};

const UnsubmitTimeApprovalModal = ({
	selectedPersonId,
	selectedPersonName,
	currentViewingDate,
	lockedDate,
	companyLockedDate,
	closeModal,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const startDateLimit = companyLockedDate && companyLockedDate.clone().add(1, 'd');
	const currentDateNoLock =
		companyLockedDate && companyLockedDate.isSameOrAfter(currentViewingDate, 'day') ? startDateLimit : currentViewingDate;

	const [selectedDate, setSelectedDate] = useState(currentDateNoLock);

	const handleConfirmButton = () => {
		trackEvent('Unsubmit Timesheet Modal Unsubmit Button', 'Clicked');
		const onSuccess = res => {
			// A refusal comes back in `errors` with `persons` null — which the unguarded
			// `persons[0]` below used to turn into a TypeError, losing the message entirely.
			if (showApprovalErrorModal(res.updateApprovalStatus?.errors, true)) return;

			// A partial failure is not a refusal: the rows that moved still moved, so the success
			// toast still runs — unless NOTHING moved, which `errors` cannot report because that
			// path reports per row.
			const nothingApplied = (res.updateApprovalStatus?.timeRegs?.length ?? 0) === 0;
			const hadFailures = showPartialApprovalFailureModal(
				res.updateApprovalStatus?.failedTimeRegs,
				intl,
				nothingApplied,
				true
			);
			if (hadFailures && nothingApplied) return;

			const personRes = res.updateApprovalStatus?.persons?.[0];
			if (personRes) {
				const newPersonLockDate = Util.CreateNonUtcMomentDate(
					personRes.submitLockedDateYear,
					personRes.submitLockedDateMonth,
					personRes.submitLockedDateDay
				);
				createToast({
					duration: 2500,
					message: `Timesheets unlocked from ${newPersonLockDate.clone().add(1, 'days').format('LL')}`,
				});
			}
			// Skipped when the partial-failure warning already REPLACED this modal: closing again
			// would close the warning too.
			if (!hadFailures) {
				closeModal();
			}
		};

		// Mutation
		Util.CommitMutation(
			UpdateApprovalStatusMutation,
			{
				personIds: [selectedPersonId],
				approvalType: APPROVAL_TYPE.UNSUBMIT,
				startYear: selectedDate.year(),
				startMonth: selectedDate.month() + 1,
				startDay: selectedDate.date(),
				includeTimeOff: true,
			},
			onSuccess
		);
	};

	const cancelButton = () => {
		trackEvent('Unsubmit Timesheet Modal Cancel Button', 'Clicked');
		closeModal();
	};

	const handleDateRangeChanged = newDate => {
		setSelectedDate(newDate ? moment(newDate) : undefined);
	};

	// When cleaning up this FF, also clean up the use of Moment in this component.

	const content = (
		<FlexColumn gap="m">
			<Text>{formatMessage({id: 'time_approval.unsubmit_timesheets_info'})}</Text>
			<DatePicker
				label={formatMessage({id: 'time_approval.unlock_from'})}
				onChange={handleDateRangeChanged.bind(this)}
				minValue={startDateLimit?.format('YYYY-MM-DD')}
				value={selectedDate?.format('YYYY-MM-DD')}
			/>
		</FlexColumn>
	);
	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={closeModal}>
			<Modal.Title>
				{selectedPersonName
					? formatMessage({id: 'time_approval.unsubmit_names_timesheets'}, {name: selectedPersonName})
					: formatMessage({id: 'time_approval.unsubmit_timesheets'})}
			</Modal.Title>
			<Modal.Content>{content}</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton
					onPress={handleConfirmButton.bind(this)}
					dataCy="unsubmit-time-save-button"
					disabled={!selectedDate}
				>
					{formatMessage({id: 'time_approval.unsubmit_timesheets'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={cancelButton.bind(this)}>
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

UnsubmitTimeApprovalModal.propTypes = {
	currentViewingDate: PropTypes.object,
	closeModal: PropTypes.func.isRequired,
};

export default UnsubmitTimeApprovalModal;
